import * as Sentry from '@sentry/nextjs'
import type { TFunction } from 'i18next'

import { Notification as IncidentNotification } from '@/graphql/appsync-notifications'
import { IncidentPriority } from '@/graphql/generated/schemas'

import { BANNER_ICON, SENTRY_TAGS, shouldShowIncidentDetail } from './constants'

export interface BannerContentIProps {
  body: string
  title: string
}

// The constructor is absent (not merely unusable) on Android Chrome/Firefox,
// on iOS Safari outside an installed PWA, and in any non-secure context.
export const isBrowserNotificationSupported = (): boolean =>
  typeof window !== 'undefined' && typeof window.Notification === 'function'

export const getBrowserPermission = (): NotificationPermission | null =>
  isBrowserNotificationSupported() ? window.Notification.permission : null

/**
 * Mirrors the in-app toast's wording (`{displayId} - {name}` + facility) so an
 * Incident reads the same in both places. When {@link shouldShowIncidentDetail}
 * is off, only the title survives — it identifies nothing on its own.
 *
 * Only Critical is called out in the title; every other priority reads "New
 * Incident", so the word Critical stays meaningful at a glance.
 */
export const buildBannerContent = (
  notification: IncidentNotification,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  t: TFunction<any>,
  shouldShowDetail: boolean = shouldShowIncidentDetail
): BannerContentIProps => {
  const title =
    notification?.incidentPriority === IncidentPriority.Critical
      ? t('incidents:browser_notification_critical_title')
      : t('incidents:browser_notification_title')

  if (!shouldShowDetail) {
    return { body: t('incidents:browser_notification_generic_body'), title }
  }

  // Every field below is nullable on the subscription payload, so the body is
  // assembled from whatever actually arrived rather than interpolating "null".
  const headline = [
    notification?.incidentDisplayId && `#${notification.incidentDisplayId}`,
    notification?.incidentName,
  ]
    .filter(Boolean)
    .join(' - ')
  const body = [headline, notification?.facilityShortName]
    .filter(Boolean)
    .join(' · ')

  return {
    body: body || t('incidents:browser_notification_generic_body'),
    title,
  }
}

interface RaiseBannerIProps {
  body: string
  /** Sentry `action` tag, so a construction failure says which path raised it. */
  action: string
  /** Omit to leave it up until clicked or dismissed — what Incidents want. */
  autoCloseMs?: number
  onClick?: () => void
  /**
   * Same-origin notifications sharing a tag collapse into one, across tabs.
   * A replacement does NOT re-alert and `renotify` doesn't reliably override
   * that, so anything which must alert every time needs a unique tag.
   */
  tag: string
  title: string
}

/**
 * Constructs an OS banner and fails soft. Shared by the Incident and test
 * paths so the two can't drift. Callers decide *whether* to raise one.
 *
 * @return whether the browser accepted it — NOT whether it was displayed.
 * Nothing can know the latter, which is why the Settings test button exists.
 */
export const raiseBanner = ({
  action,
  autoCloseMs,
  body,
  onClick,
  tag,
  title,
}: RaiseBannerIProps): boolean => {
  try {
    const banner = new window.Notification(title, {
      body,
      icon: BANNER_ICON,
      tag,
    })

    if (onClick) {
      banner.onclick = () => {
        onClick()
        banner.close()
      }
    }

    if (autoCloseMs !== undefined) {
      setTimeout(() => banner.close(), autoCloseMs)
    }

    return true
  } catch (e) {
    // Never let a banner failure break the toast/audio path around it.
    Sentry.captureException(e, { tags: { ...SENTRY_TAGS, action } })
    return false
  }
}
